import { Form, Upload } from 'antd'
import type { FormInstance } from 'antd'
import { Upload as UploadIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { useCallback } from 'react'

export interface FloorplanFormValues {
  id?: string
  code?: string
  name: string
  unitId: string
  unitName?: string
  imageFile?: any
  width?: number | string
  height?: number | string
  description?: string
}

interface FloorplanFormProps {
  form: FormInstance<FloorplanFormValues>
  mode: 'create' | 'update'
  onSubmit: (values: FloorplanFormValues) => void
  showUnitColumn: boolean
}

const PILL_INPUT =
  '!bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'
const TEXTAREA_INPUT =
  'rounded-2xl py-3 !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export function FloorplanForm({
  form,
  mode,
  showUnitColumn,
  onSubmit,
}: FloorplanFormProps) {
  const imageFileList = Form.useWatch('imageFile', form)
  const uploadedFile =
    imageFileList && imageFileList.length > 0 ? imageFileList[0] : null

  const handleFinish = (values: FloorplanFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as FloorplanFormValues

    onSubmit(trimmedValues)
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  const loadUnits = useCallback(async ({ page, signal, searchText }: any) => {
    const res = await fetchUnits(
      page,
      10,
      { keySearch: searchText?.trim() || undefined, status: 1 },
      signal,
    )
    const arr = Array.isArray(res.items) ? res.items : []
    const pageSize = 10
    const hasNextPage = arr.length >= pageSize

    return {
      options: arr.map((u: any) => ({
        value: u.id,
        label: u.name,
      })),
      hasNextPage,
      nextPage: hasNextPage ? page + 1 : undefined,
    }
  }, [])

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      requiredMark={(label, info) => (
        <>
          {label}
          {info.required && <span className="text-destructive ml-1">*</span>}
        </>
      )}
      className="space-y-6"
    >
      <Form.Item
        name="name"
        label="Tên sơ đồ"
        rules={[
          {
            required: true,
            whitespace: true,
            message: 'Vui lòng nhập tên sơ đồ',
          },
        ]}
      >
        <Input
          placeholder="Nhập tên sơ đồ"
          size="large"
          className={PILL_INPUT}
        />
      </Form.Item>

      {showUnitColumn && (
        <Form.Item
          name="unitId"
          label="Đơn vị"
          rules={[{ required: true, message: 'Vui lòng chọn đơn vị' }]}
        >
          <Select
            cacheKey="floorplan-units-select"
            initialPage={0}
            lazy
            size="large"
            placeholder="Tất cả đơn vị"
            allowClear
            className="w-full"
            enableRemoteSearch
            initialOptions={
              mode === 'update' && form.getFieldValue('unitId')
                ? [
                    {
                      value: form.getFieldValue('unitId'),
                      label: form.getFieldValue('unitName'),
                    },
                  ]
                : []
            }
            loadOptions={loadUnits}
            onLazyValueChange={(unitId) => {
              form.setFieldsValue({ unitId })
            }}
          />
        </Form.Item>
      )}

      <Form.Item
        name="imageFile"
        label="Sơ đồ"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: 'Vui lòng tải lên ảnh sơ đồ' }]}
      >
        <Upload
          beforeUpload={() => false}
          maxCount={1}
          showUploadList={false}
          accept="image/*"
          className="[&_.ant-upload-select]:!w-full"
        >
          <Button
            variant="outline"
            size="lg"
            className="w-full flex items-center justify-between border-gray-300 overflow-hidden"
          >
            <div className="flex items-center truncate">
              {uploadedFile ? (
                <>
                  <span className="truncate font-medium cursor-pointer">
                    {uploadedFile.name}
                  </span>
                </>
              ) : (
                <span className="text-gray-400 truncate">
                  Tải lên ảnh sơ đồ (PNG, JPG,...)
                </span>
              )}
            </div>
            <UploadIcon size={16} />
          </Button>
        </Upload>
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="width"
          label="Chiều rộng (m)"
          rules={[{ required: true, message: 'Vui lòng nhập chiều rộng' }]}
        >
          <Input
            type="number"
            placeholder="Nhập chiều rộng"
            size="large"
            className={PILL_INPUT}
            min={0}
          />
        </Form.Item>

        <Form.Item
          name="height"
          label="Chiều dài (m)"
          rules={[{ required: true, message: 'Vui lòng nhập chiều dài' }]}
        >
          <Input
            type="number"
            placeholder="Nhập chiều dài"
            size="large"
            className={PILL_INPUT}
            min={0}
          />
        </Form.Item>
      </div>

      <Form.Item name="description" label="Ghi chú">
        <Input.TextArea
          placeholder="Nhập ghi chú"
          size="large"
          autoSize={{ minRows: 4, maxRows: 6 }}
          className={TEXTAREA_INPUT}
        />
      </Form.Item>
    </Form>
  )
}
